import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function badgeClass(status, overdue) {
  if (overdue) return 'bg-red-100 text-red-800';
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'bg-emerald-100 text-emerald-800';
  if (s === 'paused') return 'bg-amber-100 text-amber-800';
  if (s === 'completed') return 'bg-slate-100 text-slate-700';
  if (s === 'cancelled') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
}

export default function AdminPaymentRemindersPage() {
  const [data, setData] = useState({ reminders: [], usage: [], totals: { reminders: 0, users: 0, overdue: 0 } });
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('all');
  const [overdue, setOverdue] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/payment-reminders', {
        params: {
          q: query || undefined,
          user_id: userId || undefined,
          status: status === 'all' ? undefined : status,
          overdue: overdue === 'all' ? undefined : overdue,
        },
      });
      setData(res.data || { reminders: [], usage: [], totals: { reminders: 0, users: 0, overdue: 0 } });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payment reminders');
      setData({ reminders: [], usage: [], totals: { reminders: 0, users: 0, overdue: 0 } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, userId, status, overdue]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Reminders</h1>
          <p className="text-slate-500 text-sm mt-0.5">View reminder usage across users and monitor overdue reminders.</p>
        </div>
        <Button type="button" variant="outline" onClick={load}>Refresh</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
        {[
          ['Total reminders', data?.totals?.reminders || 0],
          ['Users with reminders', data?.totals?.users || 0],
          ['Overdue reminders', data?.totals?.overdue || 0],
        ].map(([label, value]) => (
          <Card key={label} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Usage Filters</CardTitle>
          <div className="mt-3 grid gap-3 md:grid-cols-4">
            <Input placeholder="Search reminders or user id" value={query} onChange={(e) => setQuery(e.target.value)} />
            <Input placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {['active', 'paused', 'completed', 'cancelled'].map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={overdue} onValueChange={setOverdue}>
              <SelectTrigger><SelectValue placeholder="Overdue" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reminders</SelectItem>
                <SelectItem value="true">Overdue only</SelectItem>
                <SelectItem value="false">Not overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Reminder</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.reminders || []).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="font-medium text-slate-900 break-all">{item.user_id}</div>
                      <div className="text-xs text-slate-500">{item.customer?.name || '-'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{item.title || '-'}</div>
                      <div className="text-xs text-slate-500">{item.reminder_type || '-'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{item.customer?.name || '-'}</div>
                      <div className="text-xs text-slate-500">{item.customer?.phone || item.customer?.email || '-'}</div>
                    </TableCell>
                    <TableCell>{item.currency || 'UGX'} {Number(item.amount || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <div>{fmtDate(item.due_date)}</div>
                      {item.overdue ? <div className="text-xs text-red-600">Overdue</div> : null}
                    </TableCell>
                    <TableCell>
                      <Badge className={badgeClass(item.status, item.overdue)}>{item.overdue ? 'Overdue' : (item.computed_status || item.status || 'active')}</Badge>
                    </TableCell>
                    <TableCell>{item.reminder_type || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && !(data.reminders || []).length && (
            <div className="py-10 text-center text-sm text-slate-500">No reminders found.</div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Reminder Usage by User</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Overdue</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Paused</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.usage || []).map((item) => (
                <TableRow key={item.user_id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{item.owner?.name || item.user_id}</div>
                    <div className="text-xs text-slate-500">{item.owner?.email || '-'}</div>
                  </TableCell>
                  <TableCell>{item.total || 0}</TableCell>
                  <TableCell>{item.active || 0}</TableCell>
                  <TableCell>{item.overdue || 0}</TableCell>
                  <TableCell>{item.completed || 0}</TableCell>
                  <TableCell>{item.paused || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && !(data.usage || []).length && (
            <div className="py-10 text-center text-sm text-slate-500">No usage data found.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
